import { ref, computed, watch } from 'vue';
import { resolveStat, statSourcesFor, fieldMeta } from './statFields';

/**
 * StatRow (v3 homepage) — core logic, shared by both portals.
 *
 * Renders a configurable row of stat tiles. Each tile binds to a stat FIELD
 * (see statFields.js); the composable fetches whichever sources the configured
 * fields need (all via injected `http`):
 *   · `levelling`  → POST Report/levellingCounts
 *   · `activity`   → POST workflow/userActivityAlerts/
 *   · `docs`       → POST Report/docCounts  → { total, byType[], byStatus[], byTypeStatus[] }
 *
 * Doc counts come from the dedicated `Report/docCounts` endpoint (direct SQL),
 * NOT the docs store — so they're correct for BOTH index and non-index tenants
 * (the store's `docTypeSummary` carries zero counts for index tenants) and we
 * avoid loading the entire document list just to count it. The injected
 * `getDocTypeCounts`/`getStatusCounts` getters remain as an optional fallback
 * (used only if the endpoint returns nothing).
 *
 * Back-compat: with no tiles configured it falls back to the top doc-type counts.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => Array} options.getTiles   [{ icon, colour, label, statField, hint, attention, to }]
 * @param {() => Array<{name,count}>} [options.getDocTypeCounts]  optional fallback
 * @param {() => Array<{name,count}>} [options.getStatusCounts]   optional fallback
 * @param {() => void} [options.onReady]   called once after the first load settles
 */
export function statRow(options) {
  const {
    http,
    getTiles = () => [],
    getDocTypeCounts = () => [],
    getStatusCounts = () => [],
    onReady = () => {},
  } = options || {};

  const self = {
    levelling: ref(null),
    activity: ref(null),
    docCounts: ref(null), // { total, byType:[{name,count}], byStatus:[{name,count}], byTypeStatus:[{type,status,count}] }
    loading: ref(false),
  };

  // Prefer the fetched endpoint counts; fall back to any injected store getters.
  const docTypeCounts = () => {
    const d = self.docCounts.value;
    if (d && Array.isArray(d.byType) && d.byType.length) return d.byType;
    return getDocTypeCounts() || [];
  };
  const statusCounts = () => {
    const d = self.docCounts.value;
    if (d && Array.isArray(d.byStatus) && d.byStatus.length) return d.byStatus;
    return getStatusCounts() || [];
  };

  self.tiles = computed(() => {
    const configured = getTiles() || [];
    const dtc = docTypeCounts();

    // Back-compat: no tiles → top doc-type counts (the old StatRow behaviour).
    if (!configured.length) {
      if (dtc.length) {
        return dtc.slice(0, 4).map((c) => ({
          icon: 'description',
          colour: 'neutral',
          label: c.name,
          value: Number(c.count || 0).toLocaleString(),
        }));
      }
      return [{ icon: 'description', colour: 'neutral', label: 'Documents', value: '0', hint: 'Nothing here yet' }];
    }

    const ctx = {
      levelling: self.levelling.value,
      activity: self.activity.value,
      docTypeCounts: dtc,
      statusCounts: statusCounts(),
      typeStatusCounts: (self.docCounts.value && self.docCounts.value.byTypeStatus) || [],
    };
    return configured.map((t) => {
      const meta = fieldMeta(t.statField);
      // Per-tile scope: each tile can narrow its count to (include) or away from
      // (exclude) specific doc types AND/OR statuses — so one tile can count
      // "Pending Approval" for Job Profiles and another for Job Descriptions, or
      // a "Job Profile" tile can be limited to a few statuses.
      const tileTypes = Array.isArray(t.docTypes) ? t.docTypes.filter(Boolean) : [];
      const tileMode = t.docTypesMode === 'exclude' ? 'exclude' : 'include';
      const tileStatuses = Array.isArray(t.statuses) ? t.statuses.filter(Boolean) : [];
      const tileStatusMode = t.statusesMode === 'exclude' ? 'exclude' : 'include';
      const filter = {
        docTypes: tileTypes,
        docTypesMode: tileMode,
        statuses: tileStatuses,
        statusesMode: tileStatusMode,
      };
      const v = t.statField ? resolveStat(t.statField, ctx, filter) : null;
      const attention = t.attention === true || t.attention === 'true' || t.attention === 1;
      // Filter to carry when the tile links somewhere (e.g. → All Jobs). All Jobs
      // only accepts an INCLUDE list, so we resolve each scope to the explicit set
      // of values to preselect: an include scope passes straight through; an
      // exclude scope becomes its COMPLEMENT (every known value but the excluded
      // ones). A stat field that pins a value (`status:X` / `doctype:X`) presets
      // just that value. The presentation layer applies these per portal (legacy:
      // $passedParams; beta: route query — TODO).
      const sf = t.statField || '';
      const effective = (fieldVal, list, mode, universe) => {
        if (list.length) {
          if (mode === 'exclude') return universe.filter((n) => !list.some((x) => String(x) === String(n)));
          return list.slice();
        }
        return fieldVal ? [fieldVal] : [];
      };
      const statusUniverse = (ctx.statusCounts || []).map((s) => s.name).filter((n) => n != null && n !== '');
      const typeUniverse = (ctx.docTypeCounts || []).map((t2) => t2.name).filter((n) => n != null && n !== '');
      const statuses = effective(sf.startsWith('status:') ? sf.slice(7) : null, tileStatuses, tileStatusMode, statusUniverse);
      const docTypes = effective(sf.startsWith('doctype:') ? sf.slice(8) : null, tileTypes, tileMode, typeUniverse);
      return {
        icon: t.icon || meta.icon || 'insights',
        colour: attention ? 'amber' : t.colour || meta.colour || 'neutral',
        label: t.label || meta.label || '',
        value: v == null ? '—' : Number(v).toLocaleString(),
        hint: t.hint || meta.hint || '',
        attention,
        to: t.to || t.destination || '',
        statuses,
        docTypes,
      };
    });
  });

  async function fetchLevelling() {
    try {
      const r = await http.post('Report/levellingCounts', {});
      self.levelling.value = (r.data && (r.data.Data ?? r.data)) || {};
    } catch {
      self.levelling.value = {};
    }
  }
  async function fetchActivity() {
    try {
      const r = await http.post('workflow/userActivityAlerts/', {});
      self.activity.value = (r.data && (r.data.Data ?? r.data)) || {};
    } catch {
      self.activity.value = {};
    }
  }
  async function fetchDocCounts() {
    try {
      const r = await http.post('Report/docCounts', {});
      self.docCounts.value = (r.data && (r.data.Data ?? r.data)) || { total: 0, byType: [], byStatus: [], byTypeStatus: [] };
    } catch {
      self.docCounts.value = { total: 0, byType: [], byStatus: [], byTypeStatus: [] };
    }
  }

  let didReady = false;
  function signalReady() {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  }

  async function load() {
    const tiles = getTiles() || [];
    const sources = statSourcesFor(tiles);
    // Doc counts power both the docs:* tiles and the no-tiles fallback.
    const needsDocs = sources.has('docs') || !tiles.length;
    if (!sources.has('levelling') && !sources.has('activity') && !needsDocs) {
      signalReady();
      return;
    }
    self.loading.value = true;
    const jobs = [];
    if (sources.has('levelling')) jobs.push(fetchLevelling());
    if (sources.has('activity')) jobs.push(fetchActivity());
    if (needsDocs) jobs.push(fetchDocCounts());
    try {
      await Promise.all(jobs);
    } finally {
      self.loading.value = false;
      signalReady();
    }
  }

  // Fetch on create + whenever the needed sources change. Per-tile doc-type
  // scope is applied client-side from the byTypeStatus cross-tab, so it doesn't
  // require a refetch.
  watch(
    () =>
      JSON.stringify([...statSourcesFor(getTiles())]) +
      ((getTiles() || []).length ? '' : ':fallback'),
    load,
    { immediate: true },
  );

  return self;
}
